import { writable, type Writable } from 'svelte/store';
import type { StreamingAIServiceState } from '$lib/types/ai';

/**
 * AI State Service
 * Manages reactive state for AI analysis operations
 * Single Responsibility: State management and synchronization
 */
export class AIStateService {
  public readonly state: Writable<StreamingAIServiceState>;

  constructor() {
    this.state = writable<StreamingAIServiceState>({
      isInitialized: false,
      isAnalyzing: false,
      currentModel: null,
      lastError: null,
      analysisResults: null,
      tokenUsage: null,
      streaming: undefined,
      fallbackHistory: [],
      currentFallbackLevel: 'primary'
    });
  }

  /**
   * Update state with partial updates
   */
  updateState(updates: Partial<StreamingAIServiceState>): void {
    this.state.update(state => ({ ...state, ...updates }));
  }

  /**
   * Get current state synchronously
   */
  getState(): StreamingAIServiceState {
    let currentState: StreamingAIServiceState;
    this.state.subscribe(state => currentState = state)();
    return currentState!;
  }

  /**
   * Clear analysis results and reset state
   */
  clearResults(): void {
    this.updateState({
      analysisResults: null,
      lastError: null,
      tokenUsage: null
    });
  }

  /**
   * Reset to initial state
   */
  reset(): void {
    this.state.set({
      isInitialized: false,
      isAnalyzing: false,
      currentModel: null,
      lastError: null,
      analysisResults: null,
      tokenUsage: null,
      streaming: undefined,
      fallbackHistory: [],
      currentFallbackLevel: 'primary'
    });
  }

  /**
   * Set analyzing state
   */
  setAnalyzing(isAnalyzing: boolean): void {
    this.updateState({ isAnalyzing });
  }

  /**
   * Set error state
   */
  setError(error: string | null): void {
    this.updateState({ lastError: error });
  }

  /**
   * Set initialization state
   */
  setInitialized(isInitialized: boolean, model?: string | null): void {
    this.updateState({ 
      isInitialized,
      currentModel: model ?? null
    });
  }

  /**
   * Update streaming state
   */
  updateStreamingState(streaming: Partial<NonNullable<StreamingAIServiceState['streaming']>>): void {
    const currentState = this.getState();
    this.updateState({
      streaming: {
        isStreaming: false,
        chunks: [],
        currentChunk: '',
        progress: 0,
        ...currentState.streaming,
        ...streaming
      }
    });
  }

  /**
   * Set analysis results
   */
  setAnalysisResults(results: any): void {
    this.updateState({ analysisResults: results });
  }

  /**
   * Update token usage
   */
  updateTokenUsage(usage: any): void {
    this.updateState({ tokenUsage: usage });
  }

  /**
   * Add to fallback history
   */
  addToFallbackHistory(entry: any): void {
    const currentState = this.getState();
    this.updateState({
      fallbackHistory: [...(currentState.fallbackHistory || []), entry]
    });
  }

  /**
   * Set current fallback level
   */
  setFallbackLevel(level: 'primary' | 'secondary' | 'tertiary'): void {
    this.updateState({ currentFallbackLevel: level });
  }
}